import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

// 951 Bitcoin organizer pubkeys (hex)
const ORGANIZER_PUBKEYS = [
  '0e4638297b46519fcb883a0898ac003f74d72821142940710f84dcf6721d3fc6', // npub1perrs... (PerrisPlebs)
  '2d3fa5e1397c5a74eec44ca23eebf38660be95aa93437c1aa4da213a16ce4937', // npub195l6t... (951Bitcoin)
];

export interface CalendarEvent {
  id: string;
  pubkey: string;
  kind: 31922 | 31923;
  createdAt: number;
  title: string;
  summary: string;
  description: string;
  location: string;
  image: string;
  startDate: string;   // ISO date string (YYYY-MM-DD) for kind 31922, or unix timestamp string for 31923
  endDate: string;
  startTimezone: string;
  endTimezone: string;
  hashtags: string[];
  references: string[];
  dTag: string;
  raw: NostrEvent;
}

function getTag(event: NostrEvent, name: string): string {
  return event.tags.find(([t]) => t === name)?.[1] ?? '';
}

function getAllTags(event: NostrEvent, name: string): string[] {
  return event.tags.filter(([t]) => t === name).map(([, v]) => v ?? '');
}

function parseCalendarEvent(event: NostrEvent): CalendarEvent | null {
  if (event.kind !== 31922 && event.kind !== 31923) return null;

  const title = getTag(event, 'title') || getTag(event, 'name');
  const start = getTag(event, 'start');

  // Both title and start are required per NIP-52
  if (!title || !start) return null;

  return {
    id: event.id,
    pubkey: event.pubkey,
    kind: event.kind as 31922 | 31923,
    createdAt: event.created_at,
    title,
    summary: getTag(event, 'summary'),
    description: event.content,
    location: getTag(event, 'location'),
    image: getTag(event, 'image'),
    startDate: start,
    endDate: getTag(event, 'end'),
    startTimezone: getTag(event, 'start_tzid'),
    endTimezone: getTag(event, 'end_tzid'),
    hashtags: getAllTags(event, 't'),
    references: getAllTags(event, 'r'),
    dTag: getTag(event, 'd'),
    raw: event,
  };
}

// Satlantis publishes meetup events to their own relay
const SATLANTIS_RELAY = 'wss://relay.satlantis.io';

export function useCalendarEvents() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['calendar-events', ORGANIZER_PUBKEYS],
    queryFn: async () => {
      const filter = [
        {
          kinds: [31922, 31923],
          authors: ORGANIZER_PUBKEYS,
          limit: 50,
        },
      ];

      // Query both default relays AND Satlantis relay in parallel
      const [defaultEvents, satlantisEvents] = await Promise.allSettled([
        nostr.query(filter),
        nostr.relay(SATLANTIS_RELAY).query(filter),
      ]);

      const allRaw: NostrEvent[] = [
        ...(defaultEvents.status === 'fulfilled' ? defaultEvents.value : []),
        ...(satlantisEvents.status === 'fulfilled' ? satlantisEvents.value : []),
      ];

      // Deduplicate by event ID
      const seen = new Set<string>();
      const deduped = allRaw.filter((e) => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });

      const events = deduped;

      const parsed = events
        .map(parseCalendarEvent)
        .filter((e): e is CalendarEvent => e !== null);

      // Sort: upcoming first (by start date), then past events
      const now = Date.now() / 1000;
      const upcoming = parsed.filter((e) => {
        const startTs = e.kind === 31923
          ? parseInt(e.startDate, 10)
          : new Date(e.startDate).getTime() / 1000;
        return startTs >= now - 3600; // include events that started within the last hour
      });
      const past = parsed.filter((e) => {
        const startTs = e.kind === 31923
          ? parseInt(e.startDate, 10)
          : new Date(e.startDate).getTime() / 1000;
        return startTs < now - 3600;
      });

      // Sort upcoming ascending, past descending
      upcoming.sort((a, b) => {
        const aTs = a.kind === 31923 ? parseInt(a.startDate, 10) : new Date(a.startDate).getTime() / 1000;
        const bTs = b.kind === 31923 ? parseInt(b.startDate, 10) : new Date(b.startDate).getTime() / 1000;
        return aTs - bTs;
      });
      past.sort((a, b) => {
        const aTs = a.kind === 31923 ? parseInt(a.startDate, 10) : new Date(a.startDate).getTime() / 1000;
        const bTs = b.kind === 31923 ? parseInt(b.startDate, 10) : new Date(b.startDate).getTime() / 1000;
        return bTs - aTs;
      });

      return { upcoming, past, all: [...upcoming, ...past] };
    },
    staleTime: 60000,
  });
}

/** Format a CalendarEvent's start date for human display */
export function formatEventDate(event: CalendarEvent): string {
  if (event.kind === 31923) {
    // Time-based: unix timestamp
    const ts = parseInt(event.startDate, 10);
    if (isNaN(ts)) return 'Date TBD';
    const d = new Date(ts * 1000);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } else {
    // Date-based: YYYY-MM-DD
    if (!event.startDate) return 'Date TBD';
    const [year, month, day] = event.startDate.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}

/** Format a CalendarEvent's start time for human display (time-based only) */
export function formatEventTime(event: CalendarEvent): string {
  if (event.kind !== 31923) return '';
  const ts = parseInt(event.startDate, 10);
  if (isNaN(ts)) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: event.startTimezone || undefined,
  });
}

/** Check if a CalendarEvent is in the future */
export function isUpcoming(event: CalendarEvent): boolean {
  const now = Date.now() / 1000;
  const startTs = event.kind === 31923
    ? parseInt(event.startDate, 10)
    : new Date(event.startDate).getTime() / 1000;
  return startTs >= now - 3600;
}
